/**
 * Bundle Offers & Checkout Upsell
 * Replaces third-party bundle app with native theme functionality.
 *
 * Part 1: BundleOffers – checkbox list on product page. Intercepts the
 *         fetch call to /cart/add and converts the request from single-item
 *         to multi-item format, adding bundle items alongside the main product.
 * Part 2: CheckoutUpsell – popup that intercepts checkout and offers
 *         a last-chance discount on items not yet in cart.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------
     Helpers
     ------------------------------------------------------------------ */

  function fetchCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' }
    }).then(function (r) { return r.json(); });
  }

  function addItemsToCart(items, sectionIds) {
    var body = { items: items };
    if (sectionIds && sectionIds.length) {
      body.sections = sectionIds;
      body.sections_url = window.location.pathname;
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function applyDiscount(code) {
    if (!code) return Promise.resolve();
    return fetch('/discount/' + encodeURIComponent(code), {
      credentials: 'same-origin'
    });
  }

  function getCartVariantIds(cart) {
    return cart.items.map(function (i) { return i.variant_id; });
  }

  // Formats cents with a Shopify money format such as "€{{amount_with_comma_separator}}". The Liquid
  // renders the shopper's own format into data-money-format ({{ shop.money_format }} follows the currency).
  function formatMoney(cents, format) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format ? format.match(placeholder) : null;
    var rules = { // [decimals, thousands separator, decimal separator]
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_no_decimals_with_space_separator: [0, ' ', ''],
      amount_with_space_separator: [2, ' ', ','],
      amount_with_period_and_space_separator: [2, ' ', '.']
    };
    var rule = (match && rules[match[1]]) || rules.amount;
    var parts = (cents / 100).toFixed(rule[0]).split('.');
    var amount = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, rule[1]) + (parts[1] ? rule[2] + parts[1] : '');
    return match ? format.replace(placeholder, amount) : amount;
  }

  // Shopper-facing text in the shopper's language. Liquid renders each string with `t` into a data-*-text
  // attribute next to data-money-format (bundle root in sections/bundle-offers.liquid, #checkout-upsell-overlay
  // in snippets/checkout-upsell-popup.liquid), with [name] placeholders. The English literal is the fallback
  // when an attribute is missing or empty (Liquid and JS deployed out of step, or a test page without it).
  function readText(root, name, fallback) {
    var text = root && root.dataset ? root.dataset[name] : '';
    return text ? text : fallback;
  }

  // Fills every [name] in one pass with a function replacement, so a "$" in a value is never special.
  function fillText(template, values) {
    return template.replace(/\[(\w+)\]/g, function (whole, name) {
      return Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : whole;
    });
  }

  // "Sale prices are set in Shopify": the extra % the tier discounts take off the already-cut prices.
  // typed = the theme setting ("6.67, 13.33"); empty = worked out from the tier % and rounded DOWN to 0.01,
  // so a total shown is never below checkout. Tier 1 (the plain sale) has no extra discount.
  function bsExtraPercent(el, typed, pct) {
    if (!el) return 0;
    var d = el.dataset;
    var p1 = parseInt(d.p1, 10), p2 = parseInt(d.p2, 10), p3 = parseInt(d.p3, 10);
    if (pct !== p2 && pct !== p3) return 0;
    var parts = String(typed || '').split(',').map(function (s) { return parseFloat(s); });
    var given = pct === p2 ? parts[0] : parts[1];
    if (given >= 0 && given < 100) return given;
    return Math.floor(10000 * (1 - (100 - pct) / (100 - p1))) / 100;
  }

  // Black Sale tier bar (snippets/black-sale-tiers.liquid draws the first state with the same maths).
  // count = items so far; returns the percent off that count gets.
  function updateBlackSaleTiers(el, count) {
    if (!el) return 0;
    var d = el.dataset;
    var n1 = parseInt(d.n1, 10), n2 = parseInt(d.n2, 10), n3 = parseInt(d.n3, 10);
    var p1 = parseInt(d.p1, 10), p2 = parseInt(d.p2, 10), p3 = parseInt(d.p3, 10);
    var fill;
    if (count >= n3) fill = 100;
    else if (count >= n2) fill = 50 + (count - n2) * 33.33 / (n3 - n2);
    else if (count >= n1) fill = 16.67 + (count - n1) * 33.33 / (n2 - n1);
    else fill = count * 16.67 / n1;
    var fillEl = el.querySelector('[data-bs-tiers-fill]');
    if (fillEl) fillEl.style.width = fill + '%';
    el.querySelectorAll('[data-bs-tiers-step]').forEach(function (step) {
      var need = [n1, n2, n3][parseInt(step.getAttribute('data-bs-tiers-step'), 10) - 1];
      step.classList.toggle('is-reached', count >= need);
    });
    var msg;
    if (count >= n3) {
      msg = fillText(d.topText || '', { percent: p3 });
    } else {
      var left = count >= n2 ? n3 - count : n2 - count;
      var nextPct = count >= n2 ? p3 : p2;
      msg = fillText(left === 1 ? (d.oneText || '') : (d.otherText || ''), { number: left, percent: nextPct });
    }
    var msgEl = el.querySelector('[data-bs-tiers-msg]');
    // The texts are the theme's own translations (with <strong>), never shopper input.
    if (msgEl && msg) msgEl.innerHTML = msg;
    return count >= n3 ? p3 : (count >= n2 ? p2 : p1);
  }

  /* ------------------------------------------------------------------
     Part 1: Bundle Offers (Product Page)
     Strategy: Intercept window.fetch to modify /cart/add requests.
     When bundle items are checked, we convert the single-item FormData
     (id + quantity) into multi-item format (items[0][id], items[0][quantity], ...)
     This works WITH product-form.js instead of fighting against it.
     ------------------------------------------------------------------ */

  var bundleInitialized = false;

  function initBundleOffers() {
    if (bundleInitialized) return;

    var container = document.querySelector('.bundle-offers');
    if (!container) return;

    bundleInitialized = true;

    var items = container.querySelectorAll('.bundle-offers__item');
    var discountCode = container.dataset.discountCode;

    var savingsSummary = container.querySelector('.bundle-offers__savings');

    function updateBundleSavings() {
      if (!savingsSummary) return;
      var checked = container.querySelectorAll('input[type="checkbox"]:checked');
      if (container.dataset.bs === '1') {
        // Black Sale: the board plus the ticked items, at the tier that many items reach.
        // Rounded down like Shopify rounds its discount.
        var pct = updateBlackSaleTiers(container.querySelector('[data-bs-tiers]'), 1 + checked.length);
        // Show ticks per section ("✓ 2/3"), so items still ticked inside a closed section are not hidden.
        container.querySelectorAll('.bundle-offers__category').forEach(function (cat) {
          var countEl = cat.querySelector('.bundle-offers__category-count');
          if (!countEl) return;
          var all = cat.querySelectorAll('input[type="checkbox"]').length;
          var on = cat.querySelectorAll('input[type="checkbox"]:checked').length;
          countEl.textContent = on ? '\u2713 ' + on + '/' + all : '(' + all + ')';
        });
        if (!checked.length) {
          savingsSummary.style.display = 'none';
          return;
        }
        var bundleCents = parseInt(container.dataset.boardPrice, 10) || 0;
        checked.forEach(function (cb) {
          var row = cb.closest('.bundle-offers__item');
          bundleCents += row ? (parseInt(row.dataset.price, 10) || 0) : 0;
        });
        var saveCents = Math.floor(bundleCents * pct / 100);
        if (container.dataset.bsPis === '1') {
          // Sale prices set in Shopify: checkout = real prices minus the extra tier discount. The discount is
          // rounded down here, so the saving shown is never more than the real one.
          var payCents = parseInt(container.dataset.boardPay, 10) || 0;
          checked.forEach(function (cb) {
            var row = cb.closest('.bundle-offers__item');
            payCents += row ? (parseInt(row.dataset.payPrice, 10) || 0) : 0;
          });
          var extra = bsExtraPercent(container.querySelector('[data-bs-tiers]'), container.dataset.bsExtra, pct);
          saveCents = Math.max(0, bundleCents - (payCents - Math.floor(payCents * extra / 100)));
        }
        savingsSummary.textContent = fillText(
          readText(container, 'bsSavingText', 'You save [amount] on this bundle ([percent]% off)'),
          { amount: formatMoney(saveCents, container.dataset.moneyFormat), percent: pct });
        savingsSummary.style.display = '';
        return;
      }
      if (!checked.length) {
        savingsSummary.style.display = 'none';
        return;
      }
      var totalCents = 0;
      checked.forEach(function (cb) {
        var li = cb.closest('.bundle-offers__item');
        if (li && li.dataset.saveAmount) {
          totalCents += parseInt(li.dataset.saveAmount, 10);
        }
      });
      var formatted = formatMoney(totalCents, container.dataset.moneyFormat);
      savingsSummary.textContent = fillText(
        readText(container, 'savingsText', "You're saving [amount] with this bundle!"), { amount: formatted });
      savingsSummary.style.display = '';
    }

    // Toggle checked state on item click
    items.forEach(function (item) {
      var checkbox = item.querySelector('input[type="checkbox"]');

      item.addEventListener('click', function (e) {
        if (e.target.tagName === 'INPUT') return;
        checkbox.checked = !checkbox.checked;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      });

      checkbox.addEventListener('change', function () {
        item.classList.toggle('is-checked', checkbox.checked);

        // Magnetic snap animation
        item.classList.remove('is-snap');
        void item.offsetWidth;
        item.classList.add('is-snap');

        updateBundleSavings();
      });

      if (checkbox.checked) {
        item.classList.add('is-checked');
      }
    });

    // Initial savings calculation
    updateBundleSavings();

    // Per-category accordion toggles
    var toggleBtns = container.querySelectorAll('[data-bundle-toggle]');
    toggleBtns.forEach(function(btn) {
      var category = btn.closest('.bundle-offers__category');
      var itemsList = category ? category.querySelector('.bundle-offers__category-items') : null;
      if (!itemsList) return;

      // Auto-expand categories marked with data-expanded="true".
      // Black Sale: also open every category holding a ticked item, so no ticked item is hidden.
      var hasTicked = container.dataset.bs === '1' && category && category.querySelector('input[type="checkbox"]:checked');
      if (category && (category.getAttribute('data-expanded') === 'true' || hasTicked)) {
        btn.classList.add('is-open');
        itemsList.classList.add('is-open');
      }

      btn.addEventListener('click', function() {
        var isOpen = btn.classList.contains('is-open');
        btn.classList.toggle('is-open', !isOpen);
        itemsList.classList.toggle('is-open', !isOpen);
      });
    });

    // Note: Discount codes removed — use Shopify automatic discounts instead
    // so that bundle (20%) and checkout upsell (30%) discounts can stack.

    // Patch fetch to inject bundle items into /cart/add requests
    patchFetchForBundles(container);
  }

  function getCheckedBundleVariantIds(container) {
    var checked = container.querySelectorAll('input[type="checkbox"]:checked');
    var ids = [];
    checked.forEach(function (cb) {
      ids.push(cb.value);
    });
    return ids;
  }

  function patchFetchForBundles(bundleContainer) {
    var originalFetch = window.fetch;

    window.fetch = function (url, options) {
      // Only intercept /cart/add requests with FormData body
      var urlStr = (url && url.toString) ? url.toString() : '';
      var isCartAdd = urlStr.indexOf('/cart/add') !== -1;

      if (isCartAdd && options && options.body instanceof FormData) {
        var bundleIds = getCheckedBundleVariantIds(bundleContainer);

        if (bundleIds.length > 0) {
          var formData = options.body;
          var mainId = formData.get('id');
          var mainQty = formData.get('quantity') || '1';

          // Preserve sections data
          var sections = formData.get('sections');
          var sectionsUrl = formData.get('sections_url');

          // Remove single-item fields — Shopify ignores items[] when
          // standalone id/quantity are present in multipart/form-data
          formData.delete('id');
          formData.delete('quantity');

          // Add bundle items FIRST so they appear below the main product in cart
          for (var i = 0; i < bundleIds.length; i++) {
            formData.append('items[' + i + '][id]', bundleIds[i]);
            formData.append('items[' + i + '][quantity]', '1');
            formData.append('items[' + i + '][properties][_source]', 'product_page');
          }

          // Add main product LAST so it appears at the top of the cart
          var mainIdx = bundleIds.length;
          formData.append('items[' + mainIdx + '][id]', mainId);
          formData.append('items[' + mainIdx + '][quantity]', mainQty);
        }
      }

      return originalFetch.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------
     Part 2: Checkout Upsell Popup
     ------------------------------------------------------------------ */

  function initCheckoutUpsell() {
    var overlay = document.getElementById('checkout-upsell-overlay');
    if (!overlay) {
      console.log('[Checkout Upsell] No overlay element found');
      return;
    }

    var closeBtn = overlay.querySelector('.checkout-upsell__close');
    var skipBtn = overlay.querySelector('.checkout-upsell__skip');
    var addBtn = overlay.querySelector('.checkout-upsell__add-btn');
    var addBtnText = addBtn ? addBtn.querySelector('.checkout-upsell__add-btn-text') : null;
    var listEl = overlay.querySelector('.checkout-upsell__list');

    console.log('[Checkout Upsell] Initialized, items found:', overlay.querySelectorAll('.checkout-upsell__item').length);

    function closePopup() {
      overlay.classList.remove('is-active');
      document.body.style.overflow = '';
    }

    if (closeBtn) closeBtn.addEventListener('click', closePopup);
    if (skipBtn) {
      skipBtn.addEventListener('click', function () {
        closePopup();
        proceedToCheckout();
      });
    }

    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) {
        closePopup();
        proceedToCheckout();
      }
    });

    var savingsHook = overlay.querySelector('.checkout-upsell__savings-hook');
    var defaultBtnText = addBtn ? (addBtn.dataset.defaultText || 'Add items to cart') : 'Add items to cart';

    function updateFooterState() {
      // Dynamic button text
      var checked = overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked');
      var count = checked.length;

      if (addBtnText) {
        if (count === 0) {
          addBtnText.textContent = defaultBtnText;
        } else if (count === 1) {
          addBtnText.textContent = readText(overlay, 'addOneText', 'Add 1 item & save extra');
        } else {
          addBtnText.textContent = fillText(
            readText(overlay, 'addOtherText', 'Add [number] items & save extra'), { number: count });
        }
      }

      // Savings hook
      if (savingsHook) {
        if (count === 0) {
          savingsHook.style.display = 'none';
        } else {
          var totalSaveCents = 0;
          checked.forEach(function (cb) {
            var li = cb.closest('.checkout-upsell__item');
            if (li && li.dataset.saveAmount) {
              totalSaveCents += parseInt(li.dataset.saveAmount, 10);
            }
          });
          var formatted = formatMoney(totalSaveCents, overlay.dataset.moneyFormat);
          savingsHook.textContent = fillText(
            readText(overlay, 'savingsText', "You'll save an extra [amount]! Keep the streak going."), { amount: formatted });
          savingsHook.style.display = '';
        }
      }
    }

    if (listEl) {
      listEl.addEventListener('click', function (e) {
        var item = e.target.closest('.checkout-upsell__item');
        if (!item) return;
        var cb = item.querySelector('input[type="checkbox"]');
        if (!cb) return;
        if (e.target.tagName !== 'INPUT') {
          cb.checked = !cb.checked;
        }
        item.classList.toggle('is-checked', cb.checked);

        // Micro-interaction: pulse the selected item
        item.classList.remove('is-pulse');
        void item.offsetWidth; // force reflow
        item.classList.add('is-pulse');

        // Micro-interaction: pulse the CTA button to draw attention
        if (addBtn && cb.checked) {
          addBtn.classList.remove('is-pulse');
          void addBtn.offsetWidth;
          addBtn.classList.add('is-pulse');
        }

        // Update dynamic footer
        updateFooterState();
      });
    }

    if (addBtn) {
      addBtn.addEventListener('click', function () {
        var checked = overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked');
        if (!checked.length) {
          // Shake the button and show a hint
          addBtn.classList.remove('is-shake');
          void addBtn.offsetWidth;
          addBtn.classList.add('is-shake');

          if (savingsHook) {
            savingsHook.textContent = readText(overlay, 'selectHintText', 'Select at least one item to add!');
            savingsHook.style.display = '';
            savingsHook.classList.add('is-hint');
            setTimeout(function () {
              savingsHook.classList.remove('is-hint');
              if (!overlay.querySelectorAll('.checkout-upsell__item input[type="checkbox"]:checked').length) {
                savingsHook.style.display = 'none';
              }
            }, 2500);
          }
          return;
        }

        var items = [];
        checked.forEach(function (cb) {
          items.push({
            id: parseInt(cb.value, 10),
            quantity: 1,
            properties: { '_source': 'checkout_popup' }
          });
        });

        addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = readText(overlay, 'addingText', 'Adding...');

        addItemsToCart(items)
          .then(function () {
            closePopup();
            proceedToCheckout();
          })
          .catch(function (err) {
            console.error('Checkout upsell error:', err);
            closePopup();
            proceedToCheckout();
          })
          .finally(function () {
            addBtn.disabled = false;
            if (addBtnText) addBtnText.textContent = defaultBtnText;
          });
      });
    }

    // Intercept checkout buttons — use BOTH click and submit handlers
    // to ensure we catch the checkout action regardless of how it's triggered
    function handleCheckoutIntercept(e) {
      // Already showing popup, don't intercept again
      if (overlay.classList.contains('is-active')) return;

      // Query items dynamically in case they weren't rendered at init time
      var itemEls = overlay.querySelectorAll('.checkout-upsell__item');
      if (!itemEls.length) {
        console.log('[Checkout Upsell] No upsell items in popup, skipping');
        return;
      }

      console.log('[Checkout Upsell] Intercepted checkout, checking cart...');

      e.preventDefault();
      e.stopImmediatePropagation();

      fetchCart().then(function (cart) {
        var inCart = getCartVariantIds(cart);
        console.log('[Checkout Upsell] Cart variant IDs:', inCart);
        var availableItems = [];

        itemEls.forEach(function (item) {
          var cb = item.querySelector('input[type="checkbox"]');
          if (!cb) return;
          var variantId = parseInt(cb.value, 10);
          console.log('[Checkout Upsell] Checking variant', variantId, 'in cart:', inCart.indexOf(variantId) !== -1);

          if (inCart.indexOf(variantId) === -1) {
            item.style.display = '';
            cb.checked = false;
            item.classList.remove('is-checked');
            availableItems.push(item);
          } else {
            item.style.display = 'none';
          }
        });

        if (availableItems.length === 0) {
          console.log('[Checkout Upsell] All upsell items already in cart, proceeding to checkout');
          proceedToCheckout();
          return;
        }

        console.log('[Checkout Upsell] Showing popup with', availableItems.length, 'items');
        // Reset footer state for fresh popup
        updateFooterState();
        overlay.classList.add('is-active');
        document.body.style.overflow = 'hidden';
      }).catch(function (err) {
        console.error('[Checkout Upsell] Cart fetch error:', err);
        proceedToCheckout();
      });
    }

    // Capture-phase click handler for checkout buttons
    document.addEventListener('click', function (e) {
      var target = e.target.closest('#CartDrawer-Checkout, .cart__checkout-button, [name="checkout"], button[form="CartDrawer-Form"][name="checkout"]');
      if (!target) return;
      console.log('[Checkout Upsell] Checkout button clicked:', target.id || target.className);
      handleCheckoutIntercept(e);
    }, true);

    // Also capture form submit as a backup — the checkout button submits CartDrawer-Form
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form) return;
      // Check if this is the cart form being submitted for checkout
      var isCartForm = form.id === 'CartDrawer-Form' || form.id === 'cart' ||
                       (form.action && form.action.indexOf('/cart') !== -1 && form.method === 'post');
      if (!isCartForm) return;

      // Check if the submit was triggered by a checkout button (not an update button)
      var submitter = e.submitter;
      var isCheckout = submitter && (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout');
      if (!isCheckout) return;

      console.log('[Checkout Upsell] Form submit intercepted for checkout');
      handleCheckoutIntercept(e);
    }, true);
  }

  function proceedToCheckout() {
    var cartForm = document.getElementById('CartDrawer-Form');
    if (cartForm) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'checkout';
      input.value = '';
      cartForm.appendChild(input);
      cartForm.submit();
      return;
    }
    window.location.href = '/checkout';
  }

  /* ------------------------------------------------------------------
     Initialize with MutationObserver for template injection timing
     ------------------------------------------------------------------ */

  function waitForBundleAndInit() {
    initCheckoutUpsell();

    // Try immediately
    if (document.querySelector('.bundle-offers')) {
      initBundleOffers();
      return;
    }

    // The bundle section uses a <template> that injects on DOMContentLoaded.
    // Watch for the bundle container to appear in the DOM.
    var observer = new MutationObserver(function () {
      if (document.querySelector('.bundle-offers')) {
        observer.disconnect();
        initBundleOffers();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // Safety: stop watching after 10 seconds
    setTimeout(function () {
      observer.disconnect();
      initBundleOffers();
    }, 10000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForBundleAndInit);
  } else {
    waitForBundleAndInit();
  }

})();
